import { useAuth } from "@/auth/auth";
import { useOrganization } from "@/client/backend/organization";
import { useUser } from "@/client/backend/user";
import { findOwningOrganization, orderedOrganizations, orgRequestOptions, type Organization } from "@/client/director/org";
import { parsePlaygroundConfiguration } from "@/lib/playground-configuration";
import type { Limrun } from "@limrun/api/client";
import { useEffect, useMemo, useState } from "react";

function findAssetOrganization(
  assetNames: string[],
  organizations: Organization[],
  client: Limrun,
): Promise<Organization | undefined> {
  const privateAssets = assetNames.filter(name => !name.startsWith("appstore/"));
  return findOwningOrganization(organizations, async (organization) => {
    const options = orgRequestOptions(organization.tid);
    const matches = await Promise.all(privateAssets.map(async assetName => {
      const assets = await client.assets.list({ nameFilter: assetName, limit: 1 }, options);
      return assets.some(asset => asset.name === assetName);
    }));
    return matches.every(Boolean);
  }, organizations[0]);
}

// Resolve shared-link ownership before the common Playground form can be submitted.
export function usePlaygroundConfiguration(params: URLSearchParams, enabled: boolean) {
  const auth = useAuth();
  const { user, error: userError } = useUser();
  const [organization, setOrganization] = useOrganization();
  const { preview, parseError } = useMemo(() => {
    if (!enabled) return { preview: undefined, parseError: undefined };
    try {
      const configuration = parsePlaygroundConfiguration(params);
      return { preview: { configuration, assets: configuration.assets, organizationId: params.get("organization") || undefined }, parseError: undefined };
    } catch (error) {
      return { preview: null, parseError: error instanceof Error ? error.message : String(error) };
    }
  }, [params, enabled]);
  const requiresResolution = !!preview && (preview.assets.length > 0 || !!preview.organizationId || !!preview.configuration.deviceState);
  const [ready, setReady] = useState(false);
  const [resolutionError, setResolutionError] = useState<string>();

  useEffect(() => {
    if (ready || !preview || !requiresResolution) return;
    if ((user === "loading" || organization === "loading") && !userError) return;
    let cancelled = false;
    const resolveOrganization = async () => {
      try {
        if (user === "loading" || !user.emailAddress) {
          throw new Error("Could not load your email address. Reload the page to try again.");
        }
        const currentOrganization = organization === "loading" ? undefined : organization;
        const candidates = currentOrganization ? orderedOrganizations(currentOrganization, user.organizations) : [];
        const owner = preview.organizationId
          ? candidates.find(candidate => candidate.tid === preview.organizationId)
          : await findAssetOrganization(preview.assets, candidates, auth.regionClient);
        if (preview.organizationId && !owner) throw new Error("You do not have access to this preview's organization.");
        if (preview.assets.some(name => !name.startsWith("appstore/")) && !owner) {
          throw new Error(preview.assets.length > 1 ? "The preview apps must belong to the same organization." : "The preview app is not available in your organizations.");
        }
        const resolved = owner ?? currentOrganization;
        if (!cancelled && resolved) {
          if (resolved.tid !== currentOrganization?.tid) setOrganization(resolved);
          setReady(true);
        }
      } catch (error) {
        if (!cancelled) setResolutionError(error instanceof Error ? error.message : String(error));
      }
    };
    void resolveOrganization();
    return () => { cancelled = true; };
  }, [preview, requiresResolution, ready, user, userError, organization, setOrganization, auth.regionClient]);

  return {
    configuration: preview?.configuration,
    organizations: user === "loading" ? [] : user.organizations,
    attributeViewer: requiresResolution,
    viewerEmail: user === "loading" ? undefined : user.emailAddress,
    loading: enabled && requiresResolution && (!ready || user === "loading"),
    error: parseError ?? resolutionError,
  };
}
